// Letting someone know a long rebuild has finished while they are somewhere
// else in the browser. This uses the browser's own notifications, which work
// while any Syft tab is open (even in the background). Reaching someone with
// every tab closed needs a push service, which is a separate piece of work.

const OPT_IN = "syft:notify";
const WATCHING = "syft:watching";

export const notificationsSupported = () =>
  typeof window !== "undefined" && "Notification" in window;

export function notificationsEnabled(): boolean {
  if (!notificationsSupported() || Notification.permission !== "granted")
    return false;
  try {
    return localStorage.getItem(OPT_IN) === "1";
  } catch {
    return false;
  }
}

/** Ask permission (a browser prompt) and remember the answer. */
export async function enableNotifications(): Promise<boolean> {
  if (!notificationsSupported()) return false;
  const permission =
    Notification.permission === "default"
      ? await Notification.requestPermission()
      : Notification.permission;
  if (permission !== "granted") return false;
  try {
    localStorage.setItem(OPT_IN, "1");
  } catch {
    // Without storage the choice lasts only for this page, which is fine.
  }
  return true;
}

/** Job ids this browser started and has not yet heard the end of. */
export function watchedJobs(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(WATCHING) ?? "[]");
    return Array.isArray(raw)
      ? raw.filter((x): x is string => typeof x === "string")
      : [];
  } catch {
    return [];
  }
}

function saveWatched(ids: string[]) {
  try {
    localStorage.setItem(WATCHING, JSON.stringify(ids.slice(-20)));
  } catch {
    // Not available; the library still shows the status.
  }
}

export function rememberJob(jobId: string) {
  const ids = watchedJobs();
  if (!ids.includes(jobId)) saveWatched([...ids, jobId]);
}

export function forgetJob(jobId: string) {
  saveWatched(watchedJobs().filter((id) => id !== jobId));
}

export function announceReady(title: string, readingId: string) {
  if (!notificationsEnabled()) return;
  const note = new Notification("Your reading is ready", {
    body: title,
    tag: `ready-${readingId}`,
  });
  note.onclick = () => {
    window.focus();
    // eslint-disable-next-line @next/next/no-location-assign-relative-destination -- a notification click happens outside React, so there is no router to use
    window.location.assign(`/reading/${readingId}`);
  };
}
